import { createSelector } from '@reduxjs/toolkit';
import { objectValuesTyped } from '@snap-types/util/object-iterators';
import { RootState } from '@ui/store';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../utilities/selector-guards';

export const selectQuotePrice = (
  state: RootState,
  quoteLineUuid: string,
  quotePriceUuid: string,
) => {
  return getQuotePriceOrThrow(
    state.quoteBuilder,
    quoteLineUuid,
    quotePriceUuid,
  );
};

export const selectQuotePriceHasCostData = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      getQuoteLineOrThrow(state.quoteBuilder, quoteLineUuid),
  ],
  (quoteLine) => {
    const quotePrices = quoteLine.quotePrices;

    const hasAnyCostData = objectValuesTyped(quotePrices).some(
      (quotePrice) =>
        quotePrice.unitCost !== null && quotePrice.unitCost !== undefined,
    );

    const hasAllCostData = objectValuesTyped(quotePrices).every(
      (quotePrice) =>
        quotePrice.unitCost !== null && quotePrice.unitCost !== null,
    );

    return {
      hasAnyCostData,
      hasAllCostData,
    };
  },
);

export const selectQuotePriceSmallest = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      getQuoteLineOrThrow(state.quoteBuilder, quoteLineUuid),
  ],
  (quoteLine) => {
    const quotePrices = Object.values(quoteLine.quotePrices);

    if (quotePrices.length === 0) {
      return null;
    }

    return quotePrices.sort((a, b) => a.quantity.comparedTo(b.quantity))[0];
  },
);

export function quotePriceIsPending(
  quotePrice: ReturnType<typeof selectQuotePrice>,
) {
  return quotePrice.status === 'deleting' || quotePrice.status === 'creating';
}

export function quotePriceIsFailure(
  quotePrice: ReturnType<typeof selectQuotePrice>,
) {
  return (
    quotePrice.status === 'deletion-failed' ||
    quotePrice.status === 'creation-failed'
  );
}

/**
 * Is this price known to exist in Postgres?
 *
 * Quote prices are created and deleted optimistically, so the store also holds
 * prices the database does not have: one that is still being created, or whose
 * creation failed, was never written, and one whose deletion is in flight - or
 * whose delete response never made it back - may already be gone.
 */
export function quotePriceIsPersisted(
  quotePrice: ReturnType<typeof selectQuotePrice>,
) {
  return quotePrice.status === 'ready';
}

/**
 * Does this price exist only in the store? Its creation is either still in
 * flight or it failed outright, so no API response can account for it.
 */
export function quotePriceIsLocalOnly(
  quotePrice: ReturnType<typeof selectQuotePrice>,
) {
  return (
    quotePrice.status === 'creating' || quotePrice.status === 'creation-failed'
  );
}
